/**
 * The async nature of the dependencies requires that we keep
 * the state of execution in a stream that provides a synchronous follow up
 * so the registry can be kept healthy and valid
 *
 * By healthy I mean that can serve the purpose of avoid duplication of
 * modules and by valid is that each module reference corresponds to its
 * factory result
 */
import _require from './require';
import { getModule } from './registry';

let _stream = [];

/**
 * Finds a step in the stream by id
 * @param {String} id
 * @return {Object|Null} result
 */
function findInStream(id) {
  return _stream.find((step) => step.id === id);
}

/**
 * Adds a new step to the stream
 * @param {Object} step New Step
 * @return {Array} new stream
 */
export function addToStream(step) {
  const stepInStream = findInStream(step.id);

  if (stepInStream) {
    if (__DEV__) {
      // This is useful when testing in dev, so i'm going to leave the console.log
      // eslint-disable-next-line no-console,no-inline-comments
      console.log('Return step from stream:', step.id); // NOSONAR
    }
    if (typeof step.factory === 'function') {
      step.factory.apply(null, step.deps.map(getModule));
    }
    return stepInStream.promise;
  }

  step.init(step);
  _stream = _stream.concat(step);
  return step.promise;
}

/**
 * There is only one stream per instance
 * This method helps clean the stream when needed
 * @return {Array} _stream
 */
export function clearStream() {
  _stream = [];
  return _stream;
}

/**
 * Creates a promise that can be resolved by an outside agent.
 * Adds the resolve and reject as properties of the promise itself.
 * To avoid confusions its named defer
 * @return {Promise} defer promise
 */
function createDefer() {
  let res, rej;
  const promise = new Promise((resolve, reject) => {
    res = resolve;
    rej = reject;
  });
  promise.resolve = res;
  promise.reject = rej;
  return promise;
}

/**
 * Search thru the stream for steps where this step is a dependency,
 * then resolves its own dependency defer
 * @param {Object} step
 * @return {Void} null
 */
function resolveDefers(step) {

  if (__DEV__) {
    // This is useful when testing in dev, so i'm going to leave the console.log
    // eslint-disable-next-line no-console,no-inline-comments
    console.log('Resolving defers for:', step.alias || step.id); // NOSONAR
  }

  _stream.forEach(streamStep => {

    // Need to check why this deferred is not being resolved
    if (step.dependenciesDefers[streamStep.id]) {
      // Resolve defers of the step
      streamStep.promise.then(() => {
        step.dependenciesDefers[streamStep.id].resolve();
      });
    }

  });
}

/**
 * Returns a valid step
 * @param {Objec} options
 * @return {Promise} step.promise
 */
export function createStep(options) {
  return Object.assign({}, options, {

    /**
     * This HOF creates the promise chain the prevStep with self
     * @param {Object} step reference to self
     * @param {Object} prevStep previous step in the stream
     * @return {Object} step
     */
    init: function init(step) {

      if (__DEV__) {
        // This is useful when testing in dev, so i'm going to leave the console.log
        // eslint-disable-next-line no-console,no-inline-comments
        console.log('Init step:', step.id); // NOSONAR
      }

      // Build the require factories
      const depsFactories = step.deps.map((dep) => {
        const stepInStream = findInStream(dep);
        return stepInStream ? stepInStream.promise : _require(dep);
      });

      // Require and resolve the defers of all the dependencies of this step
      // Execute the callback when all the defers are resolved
      step.promise = Promise.all(depsFactories).then(() => {

        // Build the defers array from the dependenciesDefers Object
        // and exclude "exports" as it is already solved locally.
        const defersArray = step.deps.filter(dep => dep !== 'exports').map(dep =>step.dependenciesDefers[dep]);

        // Resolve self and stream defers
        resolveDefers(step);

        return Promise.all(defersArray).then(() => step.callback(step));
      });

      return step;
    },

    // Programmatically create one defer for each of the dependencies
    dependenciesDefers: (options.deps || []).reduce((defers, dep) =>
      Object.assign(defers, { [dep]: createDefer() })
    , {})
  });
}
